import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getOnboardingStatus } from '../lib/api/userApi';

const OnboardingContext = createContext();

export const useOnboarding = () => useContext(OnboardingContext);

export const OnboardingProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  // loading=true means we're checking the status
  // loading=false means we have a definitive answer (either true or false)
  const [loading, setLoading] = useState(true);
  // null = unknown, true = completed, false = not completed
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(null);
  const [checkInProgress, setCheckInProgress] = useState(false);
  const [userOnboardingData, setUserOnboardingData] = useState(null);
  
  // Check onboarding status whenever auth state changes
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // If not authenticated, we know the answer immediately
      if (!isAuthenticated) {
        setHasCompletedOnboarding(null);
        setUserOnboardingData(null);
        setLoading(false);
        return;
      }
      
      // Skip if we're already checking
      if (checkInProgress) return;
      
      // Start checking
      setLoading(true);
      setCheckInProgress(true);
      
      try {
        // Use the getOnboardingStatus function from userApi
        const { data, error } = await getOnboardingStatus();
        
        if (error) {
          console.error('Error checking onboarding status:', error);
          setHasCompletedOnboarding(null);
          setLoading(false);
          setCheckInProgress(false);
          return;
        }
        
        // Store the full onboarding data
        setUserOnboardingData(data);
        
        // Determine if onboarding is completed based on onboardingStage
        // 0 = needs onboarding, 1 or 2 = completed onboarding
        const completed = data.onboardingStage > 0;
        setHasCompletedOnboarding(completed);
        
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // On error, we don't know the status
        setHasCompletedOnboarding(null);
      } finally {
        // We now have a definitive answer (or know we can't get one)
        setLoading(false);
        setCheckInProgress(false);
      }
    };
    
    checkOnboardingStatus();
  }, [user, isAuthenticated]);
  
  // Force a new status check
  const forceStatusCheck = () => {
    if (isAuthenticated && user) {
      setLoading(true);
      setCheckInProgress(false); // This will trigger the useEffect to run again
    }
  };
  
  return (
    <OnboardingContext.Provider value={{ 
      loading, 
      hasCompletedOnboarding, 
      userOnboardingData,
      setHasCompletedOnboarding,
      resetCheck: forceStatusCheck,
      checkStatus: forceStatusCheck
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext; 